import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { trpc } from '@/trpcClient';
import { EMaterialStatus } from '@shelflix/schemas/material/enums';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shelflix/ui-web/components/ui/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Settings, Wrench } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

interface MaintenanceSectionProps {
  materialUuid: string;
  currentStatus: EMaterialStatus;
}

const _maintenanceFormSchema = z.object({
  materialUuid: z.string(),
  newStatus: z.nativeEnum(EMaterialStatus),
  maintenanceEndPrediction: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true; // Optional field
        const selectedDate = new Date(val);
        const now = new Date();
        return selectedDate > now;
      },
      { message: 'A previsão de término deve ser uma data futura' }
    ),
});

type MaintenanceFormType = z.infer<typeof _maintenanceFormSchema>;

export function MaintenanceSection({ materialUuid, currentStatus }: MaintenanceSectionProps) {
  const setMaintenanceStatusMutation = useMutation(trpc.material.setMaintenanceStatus.mutationOptions());
  const queryClient = useQueryClient();

  // Determine the target status (toggle between ACTIVE and MAINTENANCE)
  const targetStatus =
    currentStatus === EMaterialStatus.MAINTENANCE ? EMaterialStatus.ACTIVE : EMaterialStatus.MAINTENANCE;

  const form = useForm<MaintenanceFormType>({
    defaultValues: {
      materialUuid: materialUuid,
      newStatus: targetStatus,
      maintenanceEndPrediction: '',
    },
    mode: 'all',
  });

  const showMaintenanceEndPrediction = targetStatus === EMaterialStatus.MAINTENANCE;

  const onSubmit = (data: MaintenanceFormType) => {
    if (setMaintenanceStatusMutation.isPending) return;

    // Only send maintenanceEndPrediction if status is MAINTENANCE and field is filled
    // Convert datetime-local format to ISO 8601 datetime string
    let maintenanceEndPredictionISO: string | undefined;
    if (data.newStatus === EMaterialStatus.MAINTENANCE && data.maintenanceEndPrediction) {
      // datetime-local returns format like "2025-10-16T14:30"
      // We need to convert it to ISO 8601 format: "2025-10-16T14:30:00.000Z"
      const date = new Date(data.maintenanceEndPrediction);
      maintenanceEndPredictionISO = date.toISOString();
    }

    const payload = {
      materialUuid: data.materialUuid,
      newStatus: data.newStatus,
      ...(maintenanceEndPredictionISO ? { maintenanceEndPrediction: maintenanceEndPredictionISO } : {}),
    };

    setMaintenanceStatusMutation.mutate(payload, {
      onSuccess: () => {
        toast.success('Status do material atualizado com sucesso');
        queryClient.invalidateQueries({ queryKey: ['material', 'get'] });
        form.reset({ ...data, maintenanceEndPrediction: '' });
        window.location.reload();
      },
      onError: (error) => {
        toast.error(`Erro ao atualizar status: ${error.message}`);
      },
    });
  };

  return (
    <Card className="rounded-lg border border-gray-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg font-semibold text-[var(--primary)]">
          <Wrench className="h-5 w-5" />
          Gerenciar Manutenção
        </CardTitle>
        <p className="mb-3 text-sm">
          Altere o status do material para controlar sua disponibilidade.
          <br />
          Use o status <strong>Manutenção</strong> quando o equipamento estiver em reparo.
        </p>
      </CardHeader>
      <CardContent>
        <div className="mb-6 rounded-lg bg-[var(--background)] p-4">
          <div className="flex items-start gap-3">
            <Settings className="mt-0.5 h-5 w-5 text-gray-500" />
            <div>
              <h4 className="font-medium">Status Atual</h4>
              <div className="mt-1">
                <StatusBadge status={currentStatus} />
              </div>
            </div>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="newStatus"
              render={() => (
                <FormItem className="w-full">
                  <FormLabel className="flex items-start gap-3 text-sm font-medium text-[var(--gray-primary)]">
                    <Settings className="h-5 w-5 text-gray-500" />
                    <div className="flex flex-col items-start">
                      <span>Ação</span>
                      <span className="text-xs font-normal text-gray-500">
                        {currentStatus === EMaterialStatus.MAINTENANCE
                          ? 'Voltar para status ativo'
                          : 'Colocar em manutenção'}
                      </span>
                    </div>
                  </FormLabel>
                  <div className="rounded-lg border border-gray-300 bg-gray-50 p-4">
                    <p className="text-sm font-medium text-gray-700">
                      {currentStatus === EMaterialStatus.MAINTENANCE ? (
                        <>
                          O material está em <strong>Manutenção</strong>. Clique em "Atualizar Status" para marcá-lo
                          como <strong>Ativo</strong>.
                        </>
                      ) : (
                        <>
                          O material está <strong>Ativo</strong>. Clique em "Atualizar Status" para marcá-lo como em{' '}
                          <strong>Manutenção</strong>.
                        </>
                      )}
                    </p>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showMaintenanceEndPrediction && (
              <FormField
                control={form.control}
                name="maintenanceEndPrediction"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className="text-sm font-medium text-[var(--gray-primary)]">
                      Previsão de Término da Manutenção (opcional)
                    </FormLabel>
                    <FormControl>
                      <input
                        type="datetime-local"
                        {...field}
                        min={new Date().toISOString().slice(0, 16)}
                        disabled={setMaintenanceStatusMutation.isPending}
                        className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="rounded-lg border border-blue-200 bg-[var(--background)] p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-500" />
                <div>
                  <h4 className="font-medium">Informações Importantes</h4>
                  <ul className="mt-1 space-y-1 text-sm">
                    <li>• A mudança de status será registrada no histórico</li>
                    <li>• Materiais em manutenção podem não aparecer em relatórios ativos</li>
                    <li>• A previsão de término é opcional e apenas informativa</li>
                    <li>• O status pode ser alterado a qualquer momento</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <RestrictedComponent
                requiredScopes={['material:status']}
                behavior="disable"
                showToast
                toastMessage="Você não tem permissão para alterar o status de materiais."
              >
                <Button
                  type="submit"
                  loading={setMaintenanceStatusMutation.isPending}
                  disabled={setMaintenanceStatusMutation.isPending}
                  className="bg-[var(--primary)] px-6 hover:bg-[var(--primary)]"
                >
                  <Wrench className="mr-2 h-4 w-4" />
                  {setMaintenanceStatusMutation.isPending ? 'Atualizando...' : 'Atualizar Status'}
                </Button>
              </RestrictedComponent>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
